import '../../context/AuthContext.css';
import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Card } from '../../components/ui/Card';
import { 
  Download, 
  ChevronRight,
  ShoppingCart,
  CreditCard,
  Package,
  ArrowDownRight,
  RefreshCw,
  TrendingUp,
  MousePointerClick,
  Eye
} from 'lucide-react';

export default function Funnel() {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('last30');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);
  const [funnelLoading, setFunnelLoading] = useState(true);
  
  // New endpoints data - ga-events-funnel-daily & ga-events-funnel-monthly
  const [funnelDailyData, setFunnelDailyData] = useState(null);
  const [funnelDailyLoading, setFunnelDailyLoading] = useState(false);
  const [funnelMonthlyData, setFunnelMonthlyData] = useState(null);
  const [funnelMonthlyLoading, setFunnelMonthlyLoading] = useState(false);

  // Independent mini funnels (this month, last month, two months ago)
  const [miniFunnels, setMiniFunnels] = useState([
    { label: '', loading: true, data: null, funnel: { steps: [], summary: { conversionRate: 0, dropoffRate: 0 }, dropoffPairs: [] } },
    { label: '', loading: true, data: null, funnel: { steps: [], summary: { conversionRate: 0, dropoffRate: 0 }, dropoffPairs: [] } },
    { label: '', loading: true, data: null, funnel: { steps: [], summary: { conversionRate: 0, dropoffRate: 0 }, dropoffPairs: [] } }
  ]);
  
  // Funnel verisi için state
  const [funnelData, setFunnelData] = useState({
    steps: [
      { 
        name: 'View Item List',
        value: 0,
        percentage: 0,
        icon: Eye,
        dropoff: 0
      },
      { 
        name: 'Product Views',
        value: 0,
        percentage: 0,
        dropoff: 0,
        icon: MousePointerClick,
      },
      { 
        name: 'Add to Cart',
        value: 0,
        percentage: 0,
        dropoff: 0,
        icon: ShoppingCart,
      },
      { 
        name: 'View Cart',
        value: 0,
        percentage: 0,
        dropoff: 0,
        icon: ShoppingCart,
      },
      { 
        name: 'Checkout',
        value: 0,
        percentage: 0,
        dropoff: 0,
        icon: CreditCard,
      },
      { 
        name: 'Purchase',
        value: 0,
        percentage: 0,
        dropoff: 0,
        icon: Package,
      }
    ],
    summary: {
      conversionRate: 0,
      dropoffRate: 0
    }
  });

  // Helper function to get API date range parameter
  const getApiDateRangeParam = (range) => {
    switch(range) {
      case 'today': return 'today';
      case 'yesterday': return 'yesterday';
      case 'last7': return 'last7';
      case 'last30': return 'last30';
      case 'last90': return 'last90';
      case 'thisMonth': return 'thisMonth';
      case 'lastMonth': return 'lastMonth';
      case 'custom': return 'custom';
      default: return 'last30';
    }
  };

  

  const handleCustomDateChange = (dates) => {
    const [start, end] = dates;
    setCustomStartDate(start);
    setCustomEndDate(end);
    if (start && end) {
      setDateRange('custom');
      setIsCustomDateOpen(false);
    }
  };

  const formatDateYYYYMMDD = (d) => {
    if (!d) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const getMonthLabel = (year, month) => {
    const date = new Date(year, month - 1, 1);
    return date.toLocaleString('en-GB', { month: 'long', year: 'numeric' });
  };

  // Fetch all 3 mini funnels from monthly endpoint in one call
  const fetchAllMiniFunnels = async () => {
    try {
      // Set all loading
      setMiniFunnels(prev => prev.map(mf => ({ ...mf, loading: true })));
      
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        setMiniFunnels(prev => prev.map(mf => ({ ...mf, loading: false })));
        return;
      }

      // Fetch ALL months data (no date filter) to ensure we get everything
      const response = await fetch(`https://api.glowytics.com/api/google-ads/ga-events-funnel-monthly?dateRange=all`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`API Error ${response.status}`);
      }

      const data = await response.json();
      console.log('[Mini Funnels] Monthly data received:', data);
      console.log('[Mini Funnels] Available months in data:', [...new Set((data.data || []).map(d => `${d.year}-${d.month}`))].sort());

      // Get current month info
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1; // 1-12

      // Correct month calculation
      const getMonthInfo = (offset) => {
        let y = currentYear;
        let m = currentMonth - offset;
        while (m <= 0) {
          m += 12;
          y -= 1;
        }
        return { year: y, month: m };
      };

      const months = [getMonthInfo(0), getMonthInfo(1), getMonthInfo(2)];
      console.log('[Mini Funnels] Target months:', months);

      // Process each month's data
      const newMiniFunnels = months.map((monthInfo, index) => {
        const label = getMonthLabel(monthInfo.year, monthInfo.month);
        
        // Filter data for this specific month
        const monthData = (data.data || []).filter(item => 
          Number(item.year) === monthInfo.year && Number(item.month) === monthInfo.month
        );
        
        console.log(`[Mini Funnels] Month ${index}: ${label} (${monthInfo.year}-${monthInfo.month}), found ${monthData.length} records`);

        // Build summaryByEvent for this month
        const eventSummary = {};
        monthData.forEach(item => {
          if (!eventSummary[item.eventName]) {
            eventSummary[item.eventName] = { eventCount: 0, eventValue: 0 };
          }
          eventSummary[item.eventName].eventCount += item.eventCount;
          eventSummary[item.eventName].eventValue += item.eventValue;
        });

        const summaryByEvent = Object.entries(eventSummary).map(([eventName, d]) => ({
          eventName,
          totalEventCount: d.eventCount,
          totalEventValue: d.eventValue
        }));

        // Process funnel with this month's data
        const processed = processFunnelData({ summaryByEvent });

        return {
          label,
          loading: false,
          data: { summaryByEvent },
          funnel: processed
        };
      });

      setMiniFunnels(newMiniFunnels);
      console.log('[Mini Funnels] Processed:', newMiniFunnels);

    } catch (e) {
      console.error('[Mini Funnels] Error:', e);
      setMiniFunnels(prev => prev.map(mf => ({ ...mf, loading: false })));
    }
  };

  // Process funnel data from events
  const processFunnelData = (eventsData) => {
    // Define funnel steps in order: view_item_list -> view_item -> add_to_cart -> view_cart -> begin_checkout -> purchase
    const funnelSteps = [
      { eventName: 'view_item_list', displayName: 'View Item List', icon: Eye },
      { eventName: 'view_item', displayName: 'Product Views', icon: MousePointerClick },
      { eventName: 'add_to_cart', displayName: 'Add to Cart', icon: ShoppingCart },
      { eventName: 'view_cart', displayName: 'View Cart', icon: ShoppingCart },
      { eventName: 'begin_checkout', displayName: 'Checkout', icon: CreditCard },
      { eventName: 'purchase', displayName: 'Purchase', icon: Package }
    ];

    // Extract event counts from API data
    const eventCounts = {};
    if (eventsData && eventsData.summaryByEvent) {
      eventsData.summaryByEvent.forEach(event => {
        eventCounts[event.eventName] = Number(event.totalEventCount || 0);
      });
    }

    console.log('=== FUNNEL PROCESSING ===');
    console.log('Event counts:', eventCounts);

    // Find the highest event count to use as base (usually view_item)
    let baseCount = Math.max(...funnelSteps.map(step => eventCounts[step.eventName] || 0));
    
    // If no events at all, use 0 as base
    if (baseCount === 0) {
      console.log('No event data found, using zero values');
    }

    // Build funnel steps with real data (or zeros)
    const funnelStepsWithData = [];

    funnelSteps.forEach((step, index) => {
      const count = eventCounts[step.eventName] || 0;
      
      // Calculate drop-off from previous step (not from base)
      let dropoff = 0;
      if (index > 0) {
        const prevCount = funnelStepsWithData[index - 1].value;
        if (prevCount > 0) {
          dropoff = ((prevCount - count) / prevCount * 100);
        }
      }

      funnelStepsWithData.push({
        name: step.displayName,
        value: count,
        dropoff: Math.round(dropoff * 10) / 10, // Drop-off from previous step
        icon: step.icon
      });
    });

    console.log('Processed funnel steps:', funnelStepsWithData);

    // Build forward drop-off pairs (from each step to the next)
    const dropoffPairs = [];
    for (let i = 0; i < funnelStepsWithData.length - 1; i++) {
      const fromStep = funnelStepsWithData[i];
      const toStep = funnelStepsWithData[i + 1];
      const fromValue = Number(fromStep.value || 0);
      const toValue = Number(toStep.value || 0);
      const loss = Math.max(0, fromValue - toValue);
      const dropoffPct = fromValue > 0 ? Math.round(((loss / fromValue) * 100) * 10) / 10 : 0;

      dropoffPairs.push({
        fromName: fromStep.name,
        toName: toStep.name,
        fromValue,
        toValue,
        loss,
        dropoffPct
      });
    }

    // Calculate summary metrics
    const purchaseCount = eventCounts['purchase'] || 0;
    const conversionRate = baseCount > 0 ? (purchaseCount / baseCount * 100) : 0;
    const dropoffRate = 100 - conversionRate;

    const summary = {
      conversionRate: Math.round(conversionRate * 10) / 10,
      dropoffRate: Math.round(dropoffRate * 10) / 10
    };

    console.log('Funnel summary:', summary);
    console.log('Base count (highest event):', baseCount);
    console.log('=== END FUNNEL PROCESSING ===');

    return {
      steps: funnelStepsWithData,
      summary: summary,
      dropoffPairs
    };
  };

  // Fetch GA Events Funnel Daily data - MAIN FUNNEL DATA SOURCE
  const fetchFunnelDailyData = async (dateRangeParam = dateRange) => {
    try {
      setFunnelDailyLoading(true);
      setFunnelLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        console.log('[Funnel Daily] No brand ID found');
        const processedFunnel = processFunnelData(null);
        setFunnelData(processedFunnel);
        return;
      }

      const apiDateRange = getApiDateRangeParam(dateRangeParam);
      const params = new URLSearchParams({ dateRange: apiDateRange });
      if (apiDateRange === 'custom' && customStartDate && customEndDate) {
        params.set('startDate', formatDateYYYYMMDD(customStartDate));
        params.set('endDate', formatDateYYYYMMDD(customEndDate));
      }

      console.log('[Funnel Daily] Fetching with params:', params.toString());

      const response = await fetch(`https://api.glowytics.com/api/google-ads/ga-events-funnel-daily?${params.toString()}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        credentials: 'include'
      });

      if (!response.ok) throw new Error(`API Error ${response.status}`);
      const data = await response.json();
      console.log('[Funnel Daily] Data received:', data);
      setFunnelDailyData(data);

      // Process and set main funnel data
      const processedFunnel = processFunnelData(data);
      console.log('[Funnel Daily] Processed funnel:', processedFunnel);
      setFunnelData(processedFunnel);

    } catch (error) {
      console.error('[Funnel Daily] Error:', error);
      setFunnelDailyData(null);
      const processedFunnel = processFunnelData(null);
      setFunnelData(processedFunnel);
    } finally {
      setFunnelDailyLoading(false);
      setFunnelLoading(false);
    }
  };

  // Fetch GA Events Funnel Monthly data
  const fetchFunnelMonthlyData = async (rangeParam = 'all') => {
    try {
      setFunnelMonthlyLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;

      const params = new URLSearchParams({ dateRange: rangeParam });

      const response = await fetch(`https://api.glowytics.com/api/google-ads/ga-events-funnel-monthly?${params.toString()}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        credentials: 'include'
      });

      if (!response.ok) throw new Error(`API Error ${response.status}`);
      const data = await response.json();
      console.log('[Funnel Monthly] Data received:', data);
      setFunnelMonthlyData(data);
    } catch (error) {
      console.error('[Funnel Monthly] Error:', error);
      setFunnelMonthlyData(null);
    } finally {
      setFunnelMonthlyLoading(false);
    }
  };

  useEffect(() => {
    if (dateRange === 'custom' && (!customStartDate || !customEndDate)) return;
    setLoading(true);
    // Use new funnel daily endpoint for main funnel
    fetchFunnelDailyData(dateRange).finally(() => setLoading(false));
  }, [dateRange, customStartDate, customEndDate]);

  // Fetch all 3 mini funnels from monthly endpoint (single API call)
  useEffect(() => {
    fetchAllMiniFunnels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="loading-logo-container">
          <img 
            src="/logoicon.png" 
            alt="Glowytics Logo" 
            className="loading-logo"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Header */}
      <div className="flex justify-between items-center bg-white p-6 rounded-2xl shadow-sm mb-8">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl">
            <TrendingUp className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">Funnel Analysis</h1>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Date Filter Buttons */}
          <div className="flex items-center gap-1 bg-gray-50 p-0.5 rounded-lg">
            <button
              className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                dateRange === 'yesterday'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-white hover:text-gray-800'
              }`}
              onClick={() => setDateRange('yesterday')}
            >
              Yesterday
            </button>
            <button
              className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                dateRange === 'last7'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-white hover:text-gray-800'
              }`}
              onClick={() => setDateRange('last7')}
            >
              7 Days
            </button>
            <button
              className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                dateRange === 'last30'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-white hover:text-gray-800'
              }`}
              onClick={() => setDateRange('last30')}
            >
              30 Days
            </button>
            <div className="relative">
              <button
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  dateRange === 'custom'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => setIsCustomDateOpen(!isCustomDateOpen)}
              >
                Custom
              </button>
              {isCustomDateOpen && (
                <div className="absolute right-0 mt-2 z-50">
                  <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-3">
                    <DatePicker
                      selected={customStartDate}
                      onChange={handleCustomDateChange}
                      startDate={customStartDate}
                      endDate={customEndDate}
                      selectsRange
                      inline
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <button 
            onClick={() => setLoading(true)}
            className="p-2 text-gray-600 hover:text-blue-600 rounded-xl hover:bg-blue-50 transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
          </button>

          <button className="p-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors">
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>



      {/* Main Funnel View */}
      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2">
          <Card className="p-8 rounded-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-medium">Conversion Funnel</h2>
              <div className="flex items-center gap-2">
                {funnelLoading || funnelDailyLoading ? (
                  <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded flex items-center gap-1">
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    Loading...
                  </span>
                ) : (
                  <>
                    {funnelDailyData && funnelDailyData.summaryByEvent && funnelDailyData.summaryByEvent.some(e => 
                      ['view_item_list', 'view_item', 'add_to_cart', 'view_cart', 'begin_checkout', 'purchase'].includes(e.eventName)
                    ) ? (
                      <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                        Live data
                      </span>
                    ) : (
                      <span className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                        No data
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
            
            {funnelLoading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5, 6].map((_, index) => {
                  const loadingWidths = [100, 90, 78, 65, 50, 35];
                  const loadingWidth = loadingWidths[index] || 35;
                  
                  return (
                    <div key={index} className="relative">
                      <div 
                        className="bg-gray-100 rounded-xl p-4 animate-pulse"
                        style={{ 
                          width: `${loadingWidth}%`,
                          marginLeft: `${(100 - loadingWidth) / 2}%`
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-gray-200 rounded-lg"></div>
                          <div>
                            <div className="w-24 h-4 bg-gray-200 rounded mb-2"></div>
                            <div className="w-16 h-3 bg-gray-200 rounded"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-2">
                {funnelData.steps.filter(step => step.value > 0).map((step, index, filteredSteps) => {
                  // Calculate smooth funnel width based on filtered steps count
                  const calculateFunnelWidth = (stepIndex, totalSteps) => {
                    // Dynamic width calculation based on number of visible steps
                    const maxWidth = 100;
                    const minWidth = 35;
                    const step = (maxWidth - minWidth) / Math.max(totalSteps - 1, 1);
                    return Math.max(maxWidth - (stepIndex * step), minWidth);
                  };

                  const funnelWidth = calculateFunnelWidth(index, filteredSteps.length);
                  
                  return (
                    <div key={index} className="relative">
                      <div 
                        className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl p-4 transition-all hover:shadow-md relative overflow-hidden"
                        style={{ 
                          width: `${funnelWidth}%`,
                          marginLeft: `${(100 - funnelWidth) / 2}%` // Center all bars perfectly
                        }}
                      >
                        {/* Funnel shape overlay for better visual effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-xl" />
                        
                        <div className="flex items-center justify-between relative z-10">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg shadow-lg">
                              <step.icon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{step.name}</p>
                              <div className="flex items-center gap-2">
                                <p className="text-sm text-gray-500">{step.value.toLocaleString()} events</p>
                                {/* Removed vsLastPeriod (mock) */}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-gray-900">{step.value.toLocaleString()}</p>
                            <p className="text-xs text-gray-500">events</p>
                          </div>
                        </div>
                      </div>

                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="p-6 rounded-2xl">
            <h3 className="text-lg font-medium mb-4">Drop-off Analysis</h3>
            <div className="space-y-4">
              {(funnelData.dropoffPairs || []).filter(pair => pair.fromValue > 0 && pair.toValue > 0).map((pair, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-2">
                    {/* from step icon not tracked per pair; keep chevron */}
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium">{pair.fromName} → {pair.toName}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500">-{pair.loss.toLocaleString()} events</span>
                    <span className="text-sm text-red-500">{pair.dropoffPct}%</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>


        </div>
      </div>

      

      {/* Triple Conversion Funnels */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        {[0,1,2].map((i) => (
          <Card key={i} className="p-4 md:p-5 rounded-2xl">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <h3 className="text-sm md:text-base font-medium">{miniFunnels[i]?.label || 'Conversion Funnel'}</h3>
              {miniFunnels[i]?.loading ? (
                <span className="text-[10px] md:text-[11px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded flex items-center gap-1">
                  <RefreshCw className="w-3 h-3 md:w-3.5 md:h-3.5 animate-spin" />
                  Loading...
                </span>
              ) : (
                <span className="text-[10px] md:text-[11px] text-gray-500">{miniFunnels[i]?.data?.summaryByEvent?.length ? 'Live data' : 'No data'}</span>
              )}
            </div>

            {miniFunnels[i]?.loading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5, 6].map((_, index) => {
                  const loadingWidths = [100, 90, 78, 65, 50, 35];
                  const loadingWidth = loadingWidths[index] || 35;
                  return (
                    <div key={index} className="relative">
                      <div 
                        className="bg-gray-100 rounded-xl p-2 md:p-3 animate-pulse"
                        style={{ width: `${loadingWidth}%`, marginLeft: `${(100 - loadingWidth) / 2}%` }}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-7 h-7 md:w-8 md:h-8 bg-gray-200 rounded-lg"></div>
                          <div>
                            <div className="w-20 h-3 bg-gray-200 rounded mb-2"></div>
                            <div className="w-14 h-2.5 bg-gray-200 rounded"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-2">
                {(() => {
                  // Filter out steps with 0 values
                  const filteredSteps = (miniFunnels[i]?.funnel?.steps || []).filter(step => step.value > 0);
                  const allSteps = miniFunnels[i]?.funnel?.steps || [];
                  const dropoffPairs = miniFunnels[i]?.funnel?.dropoffPairs || [];
                  
                  return filteredSteps.map((step, index) => {
                    // Dynamic width based on filtered steps count
                    const maxWidth = 100;
                    const minWidth = 35;
                    const widthStep = (maxWidth - minWidth) / Math.max(filteredSteps.length - 1, 1);
                    const funnelWidth = Math.max(maxWidth - (index * widthStep), minWidth);
                    
                    // Find original index - we need the pair FROM PREVIOUS step TO this step (conversion rate into this step)
                    const originalIndex = allSteps.findIndex(s => s.name === step.name);
                    const pair = originalIndex > 0 ? dropoffPairs[originalIndex - 1] : null;
                    const isPurchase = step.name === 'Purchase';
                    
                    // Find checkout to purchase rate
                    const checkoutIndex = allSteps.findIndex(s => s.name === 'Checkout');
                    const checkoutPair = checkoutIndex >= 0 ? dropoffPairs[checkoutIndex] : null;
                    const checkoutToPurchaseRate = (isPurchase && checkoutPair && checkoutPair.fromValue > 0) 
                      ? (100 - checkoutPair.dropoffPct) 
                      : null;
                    
                    return (
                      <div key={index} className="relative">
                        <div className="relative" style={{ width: `${funnelWidth}%`, marginLeft: `${(100 - funnelWidth) / 2}%` }}>
                          <div 
                            className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl p-2.5 md:p-3 transition-all hover:shadow-md relative overflow-hidden"
                          >
                            <div className="flex items-center justify-between relative z-10">
                              <div className="flex items-center gap-3">
                                <div className="p-1.5 md:p-2 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg">
                                  <step.icon className="w-4 h-4 md:w-5 md:h-5 text-white" />
                                </div>
                                <div>
                                  <p className="text-[13px] md:text-sm font-medium text-gray-900 leading-tight">{step.name}</p>
                                  <div className="flex items-center gap-2">
                                    <p className="text-[11px] md:text-xs text-gray-500">{step.value.toLocaleString()} events</p>
                                  </div>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="flex items-center justify-end gap-2">
                                  {pair && pair.fromValue > 0 ? (
                                    <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] md:text-[11px] font-medium">
                                      {Math.max(0, 100 - pair.dropoffPct).toFixed(1)}%
                                    </span>
                                  ) : (
                                    <span className="text-[10px] md:text-[11px] text-gray-400">—</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          {isPurchase && checkoutToPurchaseRate != null && checkoutToPurchaseRate > 0 && (
                            <div className="absolute inset-y-0 left-full flex items-center ml-2">
                              <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] md:text-[11px] font-medium whitespace-nowrap">{checkoutToPurchaseRate.toFixed(1)}% from Checkout</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

 